import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { DateTime } from "@/Common/Helpers/DateTime";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { FileType } from "@/Common/Models/FileType";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { ContactDirectoryHelper } from "@/ContactDirectory/Helpers/ContactDirectoryHelper";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportableFileGenerationIntent } from "@/ExportableFile/Models/enum/ExportableFileGenerationIntent";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FileTypeConsultationRequest } from "@/ExportableFile/Models/FileTypes/FileTypeConsultationRequest";
import { FileTypeExamRequest } from "@/ExportableFile/Models/FileTypes/FileTypeExamRequest";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { SignPrintAuthorizationOverlayElement } from "@/ExportableFile/Models/OverlayElements/SignPrintAuthorizationOverlayElement";
import { FaxSignPrintExportableFileDefaultContext } from "../Components/FaxSignPrintExportableFileComponent";
import { FileTypeEncounter } from "../Models/FileTypes/FileTypeEncounter";

export class ExportableFileDispatcher {
    public static async openSignFaxPrintPopupForConsultationRequest(params: {
        patientContactId: string,
        consultationRequestId: string,
        attachedDocuments?: AttachedObjectResponse[],
        consultationRequestDate: Date,
        exportMode: ExportMode,
        isPortalSyncEnabled: boolean,
        signedPrintAuthorizations: SignedPrintAuthorization[],
        requestingStaffContactFacetId: string,
        createdByContactId?: string,
        onSignButtonCallback?: () => void,
        syncToPortalAfterAction?: (currentPreview: ExportableFile, unsignedFile?: string, signedFile?: string) => void,
        defaultContext?: FaxSignPrintExportableFileDefaultContext,
        mutateState?: (mutation: (s: { numberOfDocumentsGenerated: number, numberOfDocumentsToGenerate: number }) => void) => void,
        requestRecipient?: string,
        recipientContactDirectoryEntityId?: string,
        recipientContactDirectoryEntityType?: DirectoryEntityType,
        exportableFileGenerationIntent?: ExportableFileGenerationIntent
    }): Promise<void> {
        const exportableFile = new FileTypeConsultationRequest({
            ConsultationRequestId: params.consultationRequestId,
            description: cacheStore.GetString("ConsultationRequestLabel"),
            date: LuxonDateTimeParser.parse(params.consultationRequestDate)?.toString() ?? ""
        });

        const employee = sessionStore.getCurrentEmployee();
        const isSignable =
            (sessionStore.signOnPrintAccess || cacheStore.ClinicSettings.EnableQuickDocumentSigning)
            && (
                params.requestingStaffContactFacetId == employee.ContactFacetId
                || params.createdByContactId == employee.ContactId
            );

        const pinFrequencyModel = LoginPinFrequencyModel.fromResponseModel(employee.Model?.LoginPinFrequency);
        const pinFrequencyToken = cacheStore.ClinicSettings.EnableQuickDocumentSigning
            ? ExportableFileHelper.getPinFrequencyTokenByFileType(exportableFile.fileType, pinFrequencyModel)
            : "";

        const selectedRecipient = params.recipientContactDirectoryEntityId ?
            await ContactDirectoryHelper.getDirectoryEntity(params.recipientContactDirectoryEntityId, params.recipientContactDirectoryEntityType) :
            params.requestRecipient;

        const config = this.buildConfig({
            ...params,
            exportableFile,
            attachedDocuments: params.attachedDocuments?.map(x => ExportableFileHelper.fromAttachedObject(x)),
            isSignable,
            useLabsSearch: cacheStore.ClinicSettings.EnableMedfarDictionaryLabSearchInsteadOfDoctorSearchForBcClients,
            pinFrequencyToken,
            sentFaxItemId: params.consultationRequestId,
            selectedRecipient: selectedRecipient,
            exportableFileGenerationIntent: params.exportableFileGenerationIntent
        });

        this.dispatch(config, params.mutateState);
    }

    public static async openSignFaxPrintPopupForExamRequest(
        params: {
            patientContactId: string,
            requestId: string,
            attachedDocuments?: AttachedObjectResponse[],
            requestDate: Date,
            exportMode: ExportMode,
            isPortalSyncEnabled: boolean,
            signedPrintAuthorizations?: SignedPrintAuthorization[],
            requestingStaffContactFacetId?: string,
            createdByContactId?: string,
            onSignButtonCallback?: () => void
            syncToPortalAfterAction?: (currentPreview: ExportableFile, unsignedFile?: string, signedFile?: string) => void,
            mutateState?: (mutation: (s: { numberOfDocumentsGenerated: number, numberOfDocumentsToGenerate: number }) => void) => void,
            requestRecipient?: string,
            recipientContactDirectoryEntityId?: string,
            recipientContactDirectoryEntityType?: DirectoryEntityType
        }
    ): Promise<void> {
        const exportableFile = new FileTypeExamRequest({
            ExamRequestId: params.requestId,
            description: cacheStore.GetString("ExamRequestLabel"),
            date: LuxonDateTimeParser.parse(params.requestDate)?.toString() ?? ""
        });

        const employee = sessionStore.getCurrentEmployee();
        const isSignable =
            (sessionStore.signOnPrintAccess || cacheStore.ClinicSettings.EnableQuickDocumentSigning)
            && (
                params.requestingStaffContactFacetId == employee.ContactFacetId
                || params.createdByContactId == employee.ContactId
            );

        const pinFrequencyModel = LoginPinFrequencyModel.fromResponseModel(employee.Model?.LoginPinFrequency);
        const pinFrequencyToken = (cacheStore.ClinicSettings.EnableQuickDocumentSigning)
            ? ExportableFileHelper.getPinFrequencyTokenByFileType(exportableFile.fileType, pinFrequencyModel)
            : "";

        const selectedRecipient = params.recipientContactDirectoryEntityId ?
            await ContactDirectoryHelper.getDirectoryEntity(params.recipientContactDirectoryEntityId, params.recipientContactDirectoryEntityType) :
            params.requestRecipient;

        const config = this.buildConfig({
            ...params,
            exportableFile,
            attachedDocuments: params.attachedDocuments?.map(x => ExportableFileHelper.fromAttachedObject(x)),
            isSignable,
            pinFrequencyToken: pinFrequencyToken,
            sentFaxItemId: params.requestId,
            selectedRecipient: selectedRecipient
        });

        this.dispatch(config, params.mutateState);
    }

    public static openSignFaxPrintPopupForEncounter(
        params: {
            encounterId: string,
            patientContactId: string,
            exportMode: ExportMode,
            onSignButtonCallback?: () => void,
            mutateState?: (mutation: (s: { numberOfDocumentsGenerated: number, numberOfDocumentsToGenerate: number }) => void) => void,
            defaultContext?: FaxSignPrintExportableFileDefaultContext
        }
    ): void {
        const exportableFile = new FileTypeEncounter({
            EncounterId: params.encounterId,
            description: cacheStore.GetString("NoteDLabel"),
            date: DateTime.now()
        });

        const config = this.buildConfig({
            ...params,
            exportableFile,
            isSignable: false,
            sentFaxItemId: params.encounterId
        });

        this.dispatch(config, params.mutateState);
    }

    public static buildConfig(
        params: {
            exportableFile: ExportableFile,
            attachedDocuments?: ExportableFile[],
            exportMode: ExportMode,
            patientContactId: string,
            defaultContext?: FaxSignPrintExportableFileDefaultContext,
            isSignable: boolean,
            signedPrintAuthorizations?: SignedPrintAuthorization[],
            isPortalSyncEnabled?: boolean,
            onSignButtonCallback?: () => void,
            onPrintButtonCallback?: () => Promise<void> | void,
            syncToPortalAfterAction?: (currentPreview: ExportableFile, unsignedFile?: string, signedFile?: string) => void,
            useLabsSearch?: boolean,
            pinFrequencyToken?: string,
            sentFaxItemId?: string,
            selectedRecipient?: DirectorySelectableEntity<IDirectoryEntity> | string,
            exportableFileGenerationIntent?: ExportableFileGenerationIntent
        }
    ): IFaxPrintExportableFileConfig {
        const config: IFaxPrintExportableFileConfig = {
            patientContactId: params.patientContactId,
            exportableFile: params.exportableFile,
            attachedDocuments: params.attachedDocuments,
            exportMode: params.exportMode,
            isSyncToPortalAfterEnabled: params.isPortalSyncEnabled,
            defaultContext: params.defaultContext,
            onApplyOverlaysCallback: null,
            hideSignaturePinControl: !params.isSignable,
            isSaveSignatureEnabled: params.isSignable,
            onSignButtonCallback: params.onSignButtonCallback,
            onPrintButtonCallback: params.onPrintButtonCallback,
            syncToPortalAfterAction: params.syncToPortalAfterAction,
            useLabsSearch: params.useLabsSearch,
            pinFrequencyToken: params.pinFrequencyToken,
            hideInitialSignButton: !params.isSignable,
            sentFaxItemId: params.sentFaxItemId,
            selectedRecipient: params.selectedRecipient,
            exportableFileGenerationIntent: params.exportableFileGenerationIntent
        };

        const signedPrintAuthorization = SignedPrintAuthorization.getLast(params.signedPrintAuthorizations);
        const isSignedByCurrentUser = SignedPrintAuthorization.isSignedBy(
            signedPrintAuthorization,
            sessionStore.getCurrentEmployee().ContactFacetId
        );
        config.useSignedCopyOverride = cacheStore.ClinicSettings.EnableSignedAuthorization && isSignedByCurrentUser;

        if (params.exportMode === ExportMode.UseSigned || isSignedByCurrentUser) {
            config.preSignedCopy = params.exportableFile.clone().applyOverlayElement(new SignPrintAuthorizationOverlayElement({
                SignedPrintAuthorization: signedPrintAuthorization
            }));
        }

        return config;
    }

    public static async dispatch(config: IFaxPrintExportableFileConfig, mutateState?: (mutation: (s: { numberOfDocumentsGenerated: number, numberOfDocumentsToGenerate: number }) => void) => void): Promise<void> {
        if (config.exportMode === ExportMode.Print && config.useSignedCopyOverride) {
            if (cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && config.attachedDocuments?.any()) {
                const isFinalFile = config.exportableFile.fileType === FileType.CONSULTATIONREQUEST;

                const merged = await ExportableFileHelper.getConcatenatedFetchUrlDtoAsync(
                    config.preSignedCopy,
                    config.attachedDocuments,
                    mutateState,
                    null,
                    isFinalFile,
                    config.exportableFileGenerationIntent
                );
                PrintHelper.ShowPrintPreview((await merged?.getFetchUrlDtoAsync()).FetchUrl);
            }
            else {
                const isFinalFile = config.exportableFile.fileType === FileType.Prescription
                || config.exportableFile.fileType === FileType.CONSULTATIONREQUEST;

                config.preSignedCopy.print(true, isFinalFile, config.exportMode, config.exportableFileGenerationIntent);
            }
            return;
        }

        if (
            cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && config?.pinFrequencyToken?.length > 0
            && config.exportMode === ExportMode.Sign
            && sessionStore.hasPinSet
            && sessionStore.signOnPrintAccess
        ) {
            const isFileSigned = await this.signExportableFile(config?.pinFrequencyToken, config.exportableFile);
            config.onSignButtonCallback();
            if (isFileSigned) {
                let medicalRequestId: string = null;
                let medicalRequestType: MedicalRequestType;

                if (config.exportableFile.fileType === FileType.CONSULTATIONREQUEST) {
                    medicalRequestId = (config.exportableFile as FileTypeConsultationRequest).ConsultationRequestId;
                    medicalRequestType = MedicalRequestType.ConsultationRequest;
                }
                else if (config.exportableFile.fileType === FileType.EXAMREQUEST) {
                    medicalRequestId = (config.exportableFile as FileTypeExamRequest).ExamRequestId;
                    medicalRequestType = MedicalRequestType.LabRequest;
                }

                if (medicalRequestId && medicalRequestType == MedicalRequestType.LabRequest)
                    await ExportableFileHelper.lockMedicalRequest(medicalRequestId, medicalRequestType);

                return;
            }
        }

        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.FaxPrintExportableFile,
            { config }
        );
    }

    public static async openMultipleFilesSignFaxPrintPopup(
        params: {
            patientContactId: string,
            files: ExportableFile[],
            mutateState?: (mutation: (s: { numberOfDocumentsGenerated: number, numberOfDocumentsToGenerate: number }) => void) => void,
            onDoneMerging?: () => void;
            defaultContext?: FaxSignPrintExportableFileDefaultContext;
            useLabsSearch?: boolean; // Temporary prop to allow overriding which table to search for. This will be removed when ContactDirectory project is merged.
        }
    ): Promise<void> {
        const exportableFile = await ExportableFileHelper.mergeExportableFiles(params.files, params.mutateState);
        if (!exportableFile)
            return;

        const config: IFaxPrintExportableFileConfig = {
            exportableFile: exportableFile,
            patientContactId: params.patientContactId,
            exportMode: ExportMode.Fax,
            isSaveSignatureEnabled: false,
            isSyncToPortalAfterEnabled: false,
            syncToPortalAfterChecked: false,
            useSignedCopyOverride: false,
            hideSignaturePinControl: true,
            defaultContext: params.defaultContext,
            useLabsSearch: params.useLabsSearch,
            hideInitialSignButton: true
        };

        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.FaxPrintExportableFile,
            { config }
        );

        params?.onDoneMerging?.();
    }

    private static async signExportableFile(pinFrequencyToken: string, exportableFile: ExportableFile): Promise<boolean> {
        const isTokenValid = await ExportableFileHelper.validatePinFrequencyToken(pinFrequencyToken);

        if (isTokenValid) {
            await exportableFile.persistSignature();

            displayToast(MessageToast.Success, cacheStore.GetString("DocumentSigningPinOptions_SignedSuccessfullyLabel"));
            return true;
        }

        return isTokenValid;
    }
}
